import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
    Check, Clock, ChefHat, Bike, PackageCheck, Phone, MapPin,
    ArrowLeft, Loader2, Sparkles, Receipt, Home as HomeIcon
} from "lucide-react";
import { api } from "../lib/api";

// === Stage definitions ===
const STAGES = [
    { ids: ["pending"],                     label: "Ελήφθη",      icon: Clock },
    { ids: ["accepted", "preparing"],       label: "Ετοιμάζεται", icon: ChefHat },
    { ids: ["ready", "on_the_way"],         label: "Στον δρόμο",  icon: Bike },
    { ids: ["completed", "delivered"],      label: "Παραδόθηκε",  icon: PackageCheck },
];

// === Custom Leaflet icons ===
const storePinIcon = L.divIcon({
    className: "store-pin",
    html: `<div class="lt-pin lt-pin-store">
             <div class="lt-pin-ring"></div>
             <div class="lt-pin-disc"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"/><polyline points="9 22 9 12 15 12 15 22"/></svg></div>
           </div>`,
    iconSize: [54, 64],
    iconAnchor: [27, 56],
});

const customerPinIcon = L.divIcon({
    className: "cust-pin",
    html: `<div class="lt-pin lt-pin-cust">
             <div class="lt-pin-ring"></div>
             <div class="lt-pin-disc"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><circle cx="12" cy="10" r="3"/><path d="M12 2C7.6 2 4 5.6 4 10c0 6 8 12 8 12s8-6 8-12c0-4.4-3.6-8-8-8z"/></svg></div>
           </div>`,
    iconSize: [54, 64],
    iconAnchor: [27, 56],
});

const courierPinIcon = L.divIcon({
    className: "courier-pin",
    html: `<div class="lt-pin lt-pin-courier">
             <div class="lt-pin-pulse"></div>
             <div class="lt-pin-pulse" style="animation-delay:.5s"></div>
             <div class="lt-pin-disc"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><circle cx="5.5" cy="17.5" r="3.5"/><circle cx="18.5" cy="17.5" r="3.5"/><path d="M15 6h-2.5l-2.5 6 2 4h6"/><path d="M11 6L7 17"/></svg></div>
           </div>`,
    iconSize: [60, 60],
    iconAnchor: [30, 30],
});

// Auto-fit map to all 3 points
const FitToPoints = ({ points }) => {
    const map = useMap();
    useEffect(() => {
        if (!points?.length) return;
        const b = L.latLngBounds(points);
        map.fitBounds(b, { padding: [60, 60] });
    }, [points, map]);
    return null;
};

const formatEta = (secs) => {
    if (secs == null) return "—";
    if (secs <= 0) return "Σύντομα";
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    if (m === 0) return `${s}s`;
    return `${m}'${s.toString().padStart(2, "0")}''`;
};

export const LiveOrderTracking = () => {
    const { id } = useParams();
    const [search] = useSearchParams();
    const sessionId = search.get("session_id");
    const navigate = useNavigate();
    const [order, setOrder] = useState(null);
    const [tracking, setTracking] = useState(null);
    const [paymentMsg, setPaymentMsg] = useState("");
    const [loading, setLoading] = useState(true);
    const [now, setNow] = useState(Date.now());

    // Local 1s ticker (smooth ETA countdown)
    useEffect(() => {
        const t = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(t);
    }, []);

    const fetchAll = async () => {
        try {
            const [{ data: o }, { data: t }] = await Promise.all([
                api.get(`/orders/${id}`),
                api.get(`/orders/${id}/tracking`),
            ]);
            setOrder(o);
            setTracking(t);
            setLoading(false);
        } catch {
            navigate("/");
        }
    };

    useEffect(() => {
        fetchAll();
        const i = setInterval(fetchAll, 6000); // refresh every 6s
        return () => clearInterval(i);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    // Stripe payment status polling (when redirected back from Stripe)
    useEffect(() => {
        if (!sessionId) return;
        let attempts = 0;
        let timer;
        const poll = async () => {
            try {
                const { data } = await api.get(`/payments/status/${sessionId}`);
                if (data.payment_status === "paid") {
                    setPaymentMsg("Η πληρωμή ολοκληρώθηκε επιτυχώς!");
                    fetchAll();
                    return;
                }
                if (data.status === "expired") {
                    setPaymentMsg("Η πληρωμή έληξε. Δοκίμασε ξανά.");
                    return;
                }
                attempts += 1;
                if (attempts < 8) {
                    setPaymentMsg("Ελέγχω την πληρωμή...");
                    timer = setTimeout(poll, 2000);
                }
            } catch {
                setPaymentMsg("Σφάλμα ελέγχου πληρωμής.");
            }
        };
        poll();
        return () => timer && clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionId]);

    // Local-time ETA: snap initial server eta_seconds to a moment, then count down with `now`
    const etaAnchor = useMemo(() => {
        if (!tracking?.eta_iso) return null;
        return new Date(tracking.eta_iso).getTime();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tracking?.eta_iso]);
    const etaSecs = etaAnchor ? Math.floor((etaAnchor - now) / 1000) : null;

    if (loading || !order || !tracking) {
        return (
            <div className="min-h-screen cyber-bg grid place-items-center text-white">
                <Loader2 className="animate-spin text-[var(--gold-light)]" size={36} />
            </div>
        );
    }

    const isCancelled = order.status === "cancelled" || order.status === "rejected";
    const activeIdx = STAGES.findIndex((s) => s.ids.includes(order.status));
    const storeLoc = tracking.store.location;
    const custLoc = tracking.customer_location;
    const courierLoc = tracking.courier_location;
    const points = [
        [storeLoc.lat, storeLoc.lng],
        [courierLoc.lat, courierLoc.lng],
        [custLoc.lat, custLoc.lng],
    ];

    return (
        <div data-testid="live-tracking" className="min-h-screen cyber-bg text-white pb-12">
            {/* HEADER */}
            <header className="sticky top-0 z-30 cyber-bg/95 backdrop-blur-xl border-b border-white/5 px-5 py-4 flex items-center justify-between">
                <button
                    onClick={() => navigate("/")}
                    data-testid="tracking-back"
                    className="h-10 w-10 grid place-items-center rounded-full glass-gold border border-[var(--gold)]/20"
                >
                    <ArrowLeft size={16} className="text-[var(--gold-light)]" />
                </button>
                <div className="text-center">
                    <p className="text-[9px] font-mono uppercase tracking-[0.32em] text-white/50">// παραγγελία</p>
                    <h1 className="font-heading text-xl text-white" style={{ fontFamily: '"Cormorant Garamond", serif' }}>
                        #{order.id.slice(0, 8).toUpperCase()}
                    </h1>
                </div>
                <button
                    onClick={() => navigate("/orders")}
                    className="h-10 w-10 grid place-items-center rounded-full glass-gold border border-[var(--gold)]/20"
                >
                    <Receipt size={16} className="text-[var(--gold-light)]" />
                </button>
            </header>

            {paymentMsg && (
                <div data-testid="payment-msg" className="mx-5 mt-3 p-3 rounded-2xl border border-[var(--gold)]/30 bg-[var(--gold)]/10 text-[var(--gold-light)] text-xs">
                    {paymentMsg}
                </div>
            )}

            {/* MAP HERO */}
            <div data-testid="tracking-map" className="relative h-[55vh] sm:h-[60vh] mx-3 mt-4 rounded-3xl overflow-hidden border border-[var(--gold)]/20 shadow-[0_20px_60px_rgba(0,0,0,0.6)]">
                <MapContainer
                    center={[storeLoc.lat, storeLoc.lng]}
                    zoom={14}
                    zoomControl={false}
                    attributionControl={false}
                    style={{ height: "100%", width: "100%" }}
                >
                    <TileLayer url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png" />
                    <Polyline
                        positions={[[storeLoc.lat, storeLoc.lng], [custLoc.lat, custLoc.lng]]}
                        pathOptions={{ color: "#f4b339", weight: 3, opacity: 0.6, dashArray: "8 8" }}
                    />
                    <Marker position={[storeLoc.lat, storeLoc.lng]} icon={storePinIcon} />
                    <Marker position={[custLoc.lat, custLoc.lng]} icon={customerPinIcon} />
                    {!isCancelled && <Marker position={[courierLoc.lat, courierLoc.lng]} icon={courierPinIcon} />}
                    <FitToPoints points={points} />
                </MapContainer>

                {/* ETA Overlay */}
                {!isCancelled && (
                    <div data-testid="eta-card" className="absolute top-3 left-3 right-3 flex justify-between items-stretch gap-2">
                        <div className="glass-gold rounded-2xl px-3 py-2 border border-[var(--gold)]/30 backdrop-blur-xl">
                            <p className="text-[9px] font-mono uppercase tracking-widest text-white/55">// εκτιμώμενος χρόνος</p>
                            <p className="font-heading text-xl text-[var(--gold-light)] leading-tight" style={{ fontFamily: '"Cormorant Garamond", serif' }}>
                                {formatEta(etaSecs)}
                            </p>
                        </div>
                        <div className="glass-gold rounded-2xl px-3 py-2 border border-white/15 backdrop-blur-xl flex items-center gap-2">
                            <Sparkles size={14} className="text-[var(--gold-light)]" />
                            <span className="text-xs text-white/85">{Math.round(tracking.progress * 100)}% προόδου</span>
                        </div>
                    </div>
                )}
            </div>

            {/* STATUS TIMELINE */}
            <section data-testid="timeline" className="px-5 mt-6">
                <div className="grid grid-cols-4 gap-2">
                    {STAGES.map((s, i) => {
                        const Icon = s.icon;
                        const done = !isCancelled && i <= activeIdx;
                        const active = !isCancelled && i === activeIdx;
                        return (
                            <div key={i} data-testid={`stage-${i}`} className="flex flex-col items-center text-center">
                                <div className={`relative h-12 w-12 grid place-items-center rounded-full border-2 transition ${
                                    done
                                        ? "border-[var(--gold)] bg-gradient-to-br from-[var(--gold)] to-[#c98e15] text-[#0B0B0F]"
                                        : "border-white/15 text-white/35"
                                }`}>
                                    {active && (
                                        <span className="absolute inset-0 rounded-full bg-[var(--gold)]/30 animate-ping" />
                                    )}
                                    {done && i < activeIdx ? <Check size={18} strokeWidth={3} /> : <Icon size={18} />}
                                </div>
                                <p className={`text-[10px] font-mono uppercase tracking-widest mt-1.5 ${done ? "text-white" : "text-white/40"}`}>
                                    {s.label}
                                </p>
                            </div>
                        );
                    })}
                </div>
                {/* Connecting line */}
                <div className="relative h-1 mt-[-30px] mx-7 -z-0 hidden">
                    <div className="absolute inset-0 bg-white/10 rounded-full" />
                    <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-[var(--gold)] to-[var(--gold-light)] rounded-full transition-all" style={{ width: `${(activeIdx + 1) / STAGES.length * 100}%` }} />
                </div>
                {isCancelled && (
                    <p data-testid="cancelled-msg" className="text-center mt-4 text-[var(--gold-light)] font-mono uppercase text-xs tracking-widest">
                        {order.status === "rejected" ? "Η παραγγελία απορρίφθηκε" : "Η παραγγελία ακυρώθηκε"}
                    </p>
                )}
            </section>

            {/* STORE CARD */}
            <section data-testid="store-card" className="mx-5 mt-6 glass-gold rounded-3xl p-4 border border-white/10 flex items-center gap-3">
                {tracking.store.image_url ? (
                    <img src={tracking.store.image_url || tracking.store.logo_url} alt={tracking.store.name} className="w-14 h-14 rounded-2xl object-cover" />
                ) : (
                    <div className="w-14 h-14 rounded-2xl bg-[var(--gold)]/15 grid place-items-center">
                        <HomeIcon size={20} className="text-[var(--gold-light)]" />
                    </div>
                )}
                <div className="flex-1 min-w-0">
                    <p className="text-[9px] font-mono uppercase tracking-[0.3em] text-white/50">// κατάστημα</p>
                    <h3 className="font-heading text-lg text-white truncate" style={{ fontFamily: '"Cormorant Garamond", serif' }}>
                        {tracking.store.name}
                    </h3>
                    <p className="text-[10px] text-white/55 flex items-center gap-1 truncate">
                        <MapPin size={10} /> {storeLoc.address}
                    </p>
                </div>
                {tracking.store.phone && (
                    <a href={`tel:${tracking.store.phone}`} className="h-10 w-10 grid place-items-center rounded-full bg-[var(--gold)]/15 hover:bg-[var(--gold)]/25 transition">
                        <Phone size={14} className="text-[var(--gold-light)]" />
                    </a>
                )}
            </section>

            {/* DELIVERY ADDRESS + ORDER SUMMARY */}
            <section className="mx-5 mt-3 grid sm:grid-cols-2 gap-3">
                <div className="glass-gold rounded-3xl p-4 border border-white/10">
                    <p className="text-[9px] font-mono uppercase tracking-[0.3em] text-white/50">// διεύθυνση</p>
                    <p className="text-sm text-white mt-1">{order.address}</p>
                    <p className="text-xs text-white/60 mt-2">
                        {order.customer_name} · <span className="font-mono">{order.phone}</span>
                    </p>
                    {order.notes && (
                        <p className="text-[11px] text-[var(--gold-light)] italic mt-2">"{order.notes}"</p>
                    )}
                </div>
                <div className="glass-gold rounded-3xl p-4 border border-white/10">
                    <p className="text-[9px] font-mono uppercase tracking-[0.3em] text-white/50">// σύνοψη</p>
                    <ul className="space-y-1 mt-2">
                        {order.items.map((it) => (
                            <li key={it.item_id} className="flex justify-between text-xs">
                                <span className="text-white/85 truncate pr-2">{it.quantity}× {it.name}</span>
                                <span className="text-white/70 font-mono shrink-0">€{(it.price * it.quantity).toFixed(2)}</span>
                            </li>
                        ))}
                    </ul>
                    <div className="border-t border-white/10 mt-3 pt-2 flex justify-between text-sm">
                        <span className="text-white/60">Σύνολο</span>
                        <span className="text-[var(--gold-light)] font-mono font-bold">€{order.total.toFixed(2)}</span>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default LiveOrderTracking;
